// AICA Tabs Component
// Tabbed navigation using Radix UI
// Supports 'default' (pill) and 'underline' variants

import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cn } from '@/lib/utils';

const Tabs = TabsPrimitive.Root;

type TabsListVariant = 'default' | 'underline';

interface TabsListProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.List
> {
  variant?: TabsListVariant;
}

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      variant === 'default' &&
        'inline-flex h-10 items-center justify-center rounded-lg bg-bg-surface p-1 text-fg-muted',
      variant === 'underline' && 'flex gap-2 border-b border-border bg-bg px-6',
      className
    )}
    {...props}
  />
));
TabsList.displayName = TabsPrimitive.List.displayName;

interface TabsTriggerProps extends React.ComponentPropsWithoutRef<
  typeof TabsPrimitive.Trigger
> {
  variant?: TabsListVariant;
}

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  TabsTriggerProps
>(({ className, variant = 'default', ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Common styles
      'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium',
      'ring-offset-bg transition-all cursor-pointer',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed',
      // Default (pill) variant
      variant === 'default' && [
        'rounded-md px-3 py-1.5',
        'data-[state=active]:bg-primary-dim data-[state=active]:text-primary-fg data-[state=active]:shadow-sm',
      ],
      // Underline variant (like Project Settings). The height is this variant's
      // own: py-3 (24px) + one text-sm line (12px x 1.4 = 16.8px) + the 2px
      // underline = ~43px — about 1px lower than the old 44px floor.
      variant === 'underline' && [
        'gap-2 border-b-2 px-4 py-3',
        'border-transparent text-fg-secondary hover:text-fg',
        'data-[state=active]:border-primary data-[state=active]:text-primary',
      ],
      className
    )}
    {...props}
  />
));
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-bg',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      // Only matters with `forceMount`: Radix keeps inactive content mounted
      // then, so hide it via data-state instead of unmounting.
      'data-[state=inactive]:hidden',
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
export type { TabsListVariant };
